import React, { useEffect } from "react";
import "./PaymentLinkTermsPage.css";
import {
    PAYMENT_LINK_TERMS_PAGE_TITLE,
    PAYMENT_LINK_TERMS_TOP_ANCHOR_ID,
    YAVRIO_ADDRESS,
    YAVRIO_COMPANY_DISCLAIMER,
    YAVRIO_TAGLINE,
} from "./paymentLinkTerms.constants";
import {
    PAYMENT_LINK_TERMS_AGREEMENT,
    PAYMENT_LINK_TERMS_CONTACT,
    PAYMENT_LINK_TERMS_INTRODUCTION,
    PAYMENT_LINK_TERMS_LAST_UPDATED,
    PAYMENT_LINK_TERMS_SECTIONS,
} from "./paymentLinkTerms.content";

const EXTERNAL_LINK_ATTRIBUTES = Object.freeze({ target: "_blank", rel: "noopener noreferrer" });

function findLinkAttributes(link) {
    return link.opensInNewTab ? EXTERNAL_LINK_ATTRIBUTES : {};
}

function removeSectionNumber(heading) {
    return heading.replace(/^\d+\.\s*/, "");
}

const TermsLink = ({ link }) => {
    return (
        <a href={link.address} {...findLinkAttributes(link)}>
            {link.text}
        </a>
    );
};

const TermsTextSegment = ({ segment }) => {
    if (typeof segment === "string") {
        return segment;
    }
    return <TermsLink link={segment.link} />;
};

const TermsText = ({ segments }) => {
    return segments.map(function renderSegment(segment, segmentIndex) {
        return <TermsTextSegment key={segmentIndex} segment={segment} />;
    });
};

const TermsClause = ({ clause }) => {
    return (
        <p>
            {clause.number && <span className="terms-page__clause-number">{clause.number}</span>}
            <TermsText segments={clause.text} />
        </p>
    );
};

const TermsSection = ({ section }) => {
    return (
        <section id={section.id} className="terms-page__section">
            <h2>{section.heading}</h2>
            {section.clauses.map(function renderClause(clause, clauseIndex) {
                return <TermsClause key={clause.number || clauseIndex} clause={clause} />;
            })}
        </section>
    );
};

const TermsContents = () => {
    return (
        <nav className="terms-page__contents" aria-label="Contents">
            <h2>Contents</h2>
            <ol>
                {[...PAYMENT_LINK_TERMS_SECTIONS, PAYMENT_LINK_TERMS_CONTACT].map(function renderContentsEntry(section) {
                    return (
                        <li key={section.id}>
                            <a href={"#" + section.id}>{removeSectionNumber(section.heading)}</a>
                        </li>
                    );
                })}
            </ol>
        </nav>
    );
};

const TermsContactSection = () => {
    return (
        <section id={PAYMENT_LINK_TERMS_CONTACT.id} className="terms-page__section">
            <h2>{PAYMENT_LINK_TERMS_CONTACT.heading}</h2>
            <address>
                {PAYMENT_LINK_TERMS_CONTACT.companyName}
                <br />
                {PAYMENT_LINK_TERMS_CONTACT.postalAddress}
                <br />
                Email: <TermsLink link={PAYMENT_LINK_TERMS_CONTACT.emailLink.link} />
            </address>
        </section>
    );
};

const YavrioHeader = () => {
    return (
        <header className="terms-page__header">
            <div className="terms-page__container terms-page__header-row">
                <a href={YAVRIO_ADDRESS.WEBSITE} aria-label="Yavrio home">
                    <img className="terms-page__header-logo" src={YAVRIO_ADDRESS.LOGO} alt="Yavrio" />
                </a>
                <a className="terms-page__pill-button" href={YAVRIO_ADDRESS.WEBSITE}>
                    Visit yavr.io <span aria-hidden="true">›</span>
                </a>
            </div>
        </header>
    );
};

const YavrioFooter = () => {
    return (
        <footer className="terms-page__footer">
            <div className="terms-page__container">
                <div className="terms-page__footer-top">
                    <div className="terms-page__footer-brand">
                        <img src={YAVRIO_ADDRESS.LOGO} alt="Yavrio" />
                        <p>{YAVRIO_TAGLINE}</p>
                    </div>
                    <div className="terms-page__footer-links">
                        <div>
                            <h3>Legal</h3>
                            <ul>
                                <li>
                                    <a href={YAVRIO_ADDRESS.PRIVACY_POLICY} {...EXTERNAL_LINK_ATTRIBUTES}>
                                        Privacy Policy
                                    </a>
                                </li>
                                <li>
                                    <a href={YAVRIO_ADDRESS.TERMS_AND_CONDITIONS} {...EXTERNAL_LINK_ATTRIBUTES}>
                                        Terms &amp; Conditions
                                    </a>
                                </li>
                                <li>
                                    <a href={"#" + PAYMENT_LINK_TERMS_TOP_ANCHOR_ID}>Payment Link Terms</a>
                                </li>
                            </ul>
                        </div>
                        <div>
                            <h3>Contact</h3>
                            <ul>
                                <li>
                                    <a href={"mailto:" + YAVRIO_ADDRESS.SUPPORT_EMAIL}>{YAVRIO_ADDRESS.SUPPORT_EMAIL}</a>
                                </li>
                                <li>
                                    <a href={YAVRIO_ADDRESS.WEBSITE} {...EXTERNAL_LINK_ATTRIBUTES}>
                                        yavr.io
                                    </a>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
                <p className="terms-page__footer-disclaimer">{YAVRIO_COMPANY_DISCLAIMER}</p>
            </div>
        </footer>
    );
};

const PaymentLinkTermsPage = () => {
    useEffect(function showPageTitle() {
        const previousTitle = document.title;
        document.title = PAYMENT_LINK_TERMS_PAGE_TITLE;
        return function restorePreviousTitle() {
            document.title = previousTitle;
        };
    }, []);

    return (
        <div id={PAYMENT_LINK_TERMS_TOP_ANCHOR_ID} className="terms-page">
            <YavrioHeader />

            <div className="terms-page__hero">
                <div className="terms-page__container">
                    <div className="terms-page__tag-row">
                        <span className="terms-page__text-tag">Payment link</span>
                        <span className="terms-page__tag-label">Terms</span>
                    </div>
                    <h1>Payment Link Terms</h1>
                    <div className="terms-page__accent-bars" aria-hidden="true">
                        <span />
                        <span />
                        <span />
                    </div>
                </div>
            </div>

            <main className="terms-page__content">
                <div className="terms-page__container">
                    <article className="terms-page__terms">
                        <p className="terms-page__terms-title">Payment link terms</p>
                        <p className="terms-page__last-updated">
                            Last updated <TermsText segments={PAYMENT_LINK_TERMS_LAST_UPDATED} />
                        </p>
                        <p className="terms-page__lead">{PAYMENT_LINK_TERMS_INTRODUCTION}</p>

                        <TermsContents />

                        {PAYMENT_LINK_TERMS_SECTIONS.map(function renderSection(section) {
                            return <TermsSection key={section.id} section={section} />;
                        })}
                        <TermsContactSection />

                        <p className="terms-page__agreement">{PAYMENT_LINK_TERMS_AGREEMENT}</p>
                    </article>
                </div>
            </main>

            <YavrioFooter />

            <a className="terms-page__back-to-top" href={"#" + PAYMENT_LINK_TERMS_TOP_ANCHOR_ID} aria-label="Back to top">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M12 19V5M5 12l7-7 7 7" />
                </svg>
            </a>
        </div>
    );
};

export default PaymentLinkTermsPage;
